import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomTabs from "../components/tabs/CustomTabs";
import { Event, ServerCredentials } from "../pages/WaveMonitor";
import EventTab from "./EventTab";
import { VideoTab } from "./VideoTab";
import { MapTab } from "./MapTab";
import { TranscriptTab } from "./TranscriptTab";
import { Device } from "../interfaces/Device";

interface Props {
  alarmList: Event[];
  eventList: Event[];
  ecgList: Event[];
  msgList: Event[];
  imageList: Event[];
  videoId: string;
  onEventSelected: (event: Event) => void;
  style?: React.CSSProperties;
  device: Device;
  showLive?: boolean;
  serverCredentials?: ServerCredentials;
  accessToken?: string;
}

export const EventTable: React.FC<Props> = ({
  device,
  videoId,
  eventList,
  ecgList,
  msgList,
  imageList,
  onEventSelected,
  showLive = true,
  serverCredentials,
  accessToken,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const tabNames = useMemo(() => {
    const baseTabs = [
      t("Events"),
      t("ECG"),
      t("Msg"),
      t("Images"),
      t("Transcripts"),
    ];
    const dynamicTabs = [
      ...(videoId && showLive ? [t("Video")] : []),
      ...(showLive ? [t("Map")] : []),
    ];
    return [...baseTabs, ...dynamicTabs];
  }, [t, videoId, showLive]);

  const validatedActiveTab = activeTab > tabNames.length - 1 ? 0 : activeTab;

  const prevLengthsRef = useRef<number[]>([
    eventList?.length ?? 0,
    ecgList?.length ?? 0,
    msgList?.length ?? 0,
    imageList?.length ?? 0,
  ]);

  const badgeStorageKey = `wavemonitor-event-badges-${device.device_id}`;
  const [badgeCounts, setBadgeCounts] = useState<number[]>(() => {
    try {
      const savedBadges = window.localStorage.getItem(badgeStorageKey);
      if (!savedBadges) {
        return new Array(4).fill(0);
      }

      const parsedBadges = JSON.parse(savedBadges);
      return Array.isArray(parsedBadges) && parsedBadges.every((count) => typeof count === "number")
        ? parsedBadges
        : new Array(4).fill(0);
    } catch {
      return new Array(4).fill(0);
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(badgeStorageKey, JSON.stringify(badgeCounts));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [badgeCounts, badgeStorageKey]);

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
    setBadgeCounts((prev) => prev.map((count, idx) => (idx === newValue ? 0 : count)));
  };

  useEffect(() => {
    const lists = [eventList, ecgList, msgList, imageList];
    const currentLengths = lists.map((list) => list?.length ?? 0);

    setBadgeCounts((prevCounts) => {
      const normalizedPrev = prevCounts.slice(0, lists.length);
      while (normalizedPrev.length < lists.length) {
        normalizedPrev.push(0);
      }

      const updated = lists.map((_, idx) => {
        const prevLength = prevLengthsRef.current[idx] ?? 0;
        const currentLength = currentLengths[idx];

        if (validatedActiveTab === idx) {
          return 0;
        }

        if (currentLength > prevLength) {
          const delta = currentLength - prevLength;
          return (normalizedPrev[idx] ?? 0) + delta;
        }

        if (currentLength < prevLength) {
          const delta = prevLength - currentLength;
          return Math.max(0, (normalizedPrev[idx] ?? 0) - delta);
        }

        return normalizedPrev[idx] ?? 0;
      });

      return updated;
    });

    prevLengthsRef.current = currentLengths;
  }, [eventList, ecgList, msgList, imageList, validatedActiveTab]);

  const tabBadges = useMemo(() => {
    return tabNames.map((_, idx) => (idx < badgeCounts.length ? badgeCounts[idx] : undefined));
  }, [badgeCounts, tabNames]);

  useEffect(() => {
    if (activeTab !== validatedActiveTab) {
      setActiveTab(validatedActiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedActiveTab]);

  const tabComponents = useMemo(() => {
    const baseComponents = [
      <EventTab key="events" events={eventList} onEventSelected={onEventSelected} />,
      <EventTab key="ecg" events={ecgList} onEventSelected={onEventSelected} />,
      <EventTab key="msg" events={msgList} onEventSelected={onEventSelected} />,
      <EventTab key="images" events={imageList} onEventSelected={onEventSelected} />,
      <TranscriptTab
        key="transcripts"
        deviceId={device.device_id}
        accessToken={accessToken}
      />,
    ];

    const dynamicComponents = [
      ...(videoId && showLive
        ? [
            <VideoTab
              key="video"
              stream={videoId}
              video_server={serverCredentials?.webrtc.server_url}
              channel_id="0"
              authToken={serverCredentials?.webrtc.auth_token}
            />,
          ]
        : []),
      ...(showLive ? [<MapTab key="map" device={device} />] : []),
    ];

    return [...baseComponents, ...dynamicComponents];
  }, [
    device,
    eventList,
    ecgList,
    msgList,
    imageList,
    onEventSelected,
    videoId,
    showLive,
    serverCredentials,
    accessToken,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CustomTabs
        tabNames={tabNames}
        tabComponents={tabComponents}
        value={validatedActiveTab}
        onTabChange={handleTabChange}
        tabBadges={tabBadges}
      />
    </Box>
  );
};

export default EventTable;
